import { useState } from 'react';
import { IconBeach, IconBell, IconBriefcase, IconClockPlus, IconEdit } from '@tabler/icons-react';
import { HeaderTool, useLayout } from '@hana/shell';
import { useT } from '@hana/i18n';
import './common.i18n';
import { Chip, Segmented, type Tone } from '@hana/ui';
import type { MeeRequest, RequestStatus, RequestType } from './data';
import s from './mee.module.css';

export const TYPE_ICON: Record<RequestType, { icon: typeof IconBeach; label: string }> = {
  leave: { icon: IconBeach, label: 'Nghỉ phép' },
  ot: { icon: IconClockPlus, label: 'Tăng ca' },
  trip: { icon: IconBriefcase, label: 'Công tác' },
  fix: { icon: IconEdit, label: 'Giải trình công' },
};

export function TypeIcon({ type }: { type: RequestType }) {
  const t = TYPE_ICON[type];
  return (
    <span className={s.ic} title={t.label} role="img" aria-label={t.label}>
      <t.icon size={18} stroke={1.8} />
    </span>
  );
}

const STATUS: Record<RequestStatus, { k: string; tone: Tone; dot: string }> = {
  Pending: { k: 'Chờ duyệt', tone: 'warn', dot: 'var(--d-amber)' },
  Approved: { k: 'Đã duyệt', tone: 'ok', dot: 'var(--d-green)' },
  Rejected: { k: 'Từ chối', tone: 'err', dot: 'var(--err-bd)' },
  Cancelled: { k: 'Đã rút', tone: 'neutral', dot: 'var(--d-gray)' },
};
export const StatusChip = ({ status }: { status: RequestStatus }) => (
  <Chip tone={STATUS[status].tone} dot={STATUS[status].dot}>
    {STATUS[status].k}
  </Chip>
);

/** Chuỗi duyệt theo ghế: bước đầu là người đang duyệt khi đơn chờ. */
export function RouteSteps({ route, status }: { route: ReadonlyArray<readonly [string, string, string]>; status: MeeRequest['status'] }) {
  return (
    <span className={s.steps}>
      {route.map(([seat, who, note], i) => (
        <span key={seat} title={`${seat} · ${who} — ${note}`}>
          <Chip tone={status === 'Approved' ? 'ok' : status === 'Pending' && i === 0 ? 'warn' : 'neutral'}>{seat}</Chip>
        </span>
      ))}
    </span>
  );
}

/** Vòng phép năm: đã dùng · chờ duyệt · còn lại. */
export function LeaveDonut({ quota, used, pending, left }: { quota: number; used: number; pending: number; left: number }) {
  const t = useT();
  const R = 44;
  const C = 2 * Math.PI * R;
  const free = Math.max(0, left - pending);
  const parts = [
    { v: used, c: 'var(--pri)' },
    { v: pending, c: 'var(--d-amber)' },
    { v: free, c: 'var(--d-gray)' },
  ];
  let off = 0;
  return (
    <svg className={s.donut} width="120" height="120" viewBox="0 0 120 120" role="img" aria-label={`Phép năm: đã dùng ${used}, chờ duyệt ${pending}, còn lại ${left} trên ${quota} ngày`}>
      <g transform="rotate(-90 60 60)" fill="none" strokeWidth="14">
        {parts.map((p, i) => {
          const len = quota > 0 ? (p.v / quota) * C : 0;
          const el = <circle key={i} cx="60" cy="60" r={R} stroke={p.c} strokeDasharray={`${len} ${C - len}`} strokeDashoffset={-off} />;
          off += len;
          return el;
        })}
      </g>
      <text x="60" y="62" textAnchor="middle" className={s.donutNum}>
        {String(left).replace('.', ',')}
      </text>
      <text x="60" y="78" textAnchor="middle" className={s.donutLb}>
        {t('mc.daysLeft')}
      </text>
    </svg>
  );
}

type Density = 'std' | 'compact';

/** Mật độ + Thông báo của tôi trên đầu trang mọi tab Mee (board Mint-Mee; điện thoại không có) */
export function MeeHeaderTools({ pending, density: d, onDensity }: { pending?: number; density?: Density; onDensity?: (v: Density) => void }) {
  const [own, setOwn] = useState<Density>('std');
  const density = d ?? own;
  const setDensity = onDensity ?? setOwn;
  if (useLayout() === 'phone') return null;
  return (
    <>
      <Segmented kind="toggle" label="Mật độ" value={density} onChange={setDensity} items={[{ key: 'std', label: 'Chuẩn' }, { key: 'compact', label: 'Gọn' }]} />
      <HeaderTool label="Thông báo của tôi" icon={IconBell} n={pending || undefined} />
    </>
  );
}
